import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Building2,
  Loader2,
  LogOut,
  Plus,
  Search,
  UserRound,
  Users,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { invariant } from "ts-invariant";

import { AuthShell } from "@/components/AuthShell";
import { BrandMark } from "@/components/BrandMark";
import { OrganizationSwitcher } from "@/components/OrganizationSwitcher";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Skeleton } from "@/components/ui/skeleton";
import { bearAuthClient } from "@/lib/bear-auth-client";
import { useSession, useSignedInUser } from "@/lib/session";

type AccessReason =
  | "owner_organization"
  | "shared_organization"
  | "direct_share";

interface ProjectSummary {
  id: string;
  name: string;
  owner: { id: string; name: string };
  accessReasons: AccessReason[];
  canManage: boolean;
}

/**
 * Yours are the acting organization's projects.
 * Shared are the ones you reach only through a share. Your other
 * organizations' projects wait until you switch to that organization.
 */
function groupProjects(
  projects: ProjectSummary[],
  organizationId: string | null,
): { own: ProjectSummary[]; shared: ProjectSummary[] } {
  const owned = (p: ProjectSummary) =>
    p.accessReasons.includes("owner_organization");
  return {
    own: projects.filter(
      (p) =>
        owned(p) && (organizationId === null || p.owner.id === organizationId),
    ),
    shared: projects.filter((p) => !owned(p)),
  };
}

function sharedLabel(project: ProjectSummary): string {
  return project.accessReasons.includes("direct_share")
    ? `Owned by ${project.owner.name}, shared with you`
    : `Owned by ${project.owner.name}, shared with your organization`;
}

function matches(project: ProjectSummary, search: string): boolean {
  const needle = search.trim().toLowerCase();
  return [project.name, project.owner.name].some((text) =>
    text.toLowerCase().includes(needle),
  );
}

interface IndexLoaderData {
  projects: ProjectSummary[] | null;
}

export const Route = createFileRoute("/")({
  loader: async (): Promise<IndexLoaderData> => {
    const res = await fetch("/api/projects", { credentials: "include" });
    if (res.status === 401) {
      return { projects: null };
    }
    if (!res.ok) {
      throw new Error("Failed to load projects");
    }
    const data = (await res.json()) as { projects: ProjectSummary[] };
    return { projects: data.projects };
  },
  component: Index,
});

function Index() {
  const { projects } = Route.useLoaderData();
  const { user } = useSession();
  return user ? (
    <SignedInLanding initialProjects={projects} />
  ) : (
    <SignedOutLanding />
  );
}

/** Invitations waiting on someone with no organization yet, e.g. an invitee
 *  back through a fresh sign-in link rather than the invitation's own. */
function PendingInvitations() {
  const invitations = useQuery({
    queryKey: ["user-invitations"],
    queryFn: async () => {
      const result = await bearAuthClient.organization.listUserInvitations();
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });
  return (invitations.data ?? []).map((invitation) => (
    <Button key={invitation.id} asChild>
      <Link to="/invite/$invitationId" params={{ invitationId: invitation.id }}>
        Join {invitation.organizationName}
      </Link>
    </Button>
  ));
}

interface SignedInLandingProps {
  initialProjects: ProjectSummary[] | null;
}

function SignedInLanding({ initialProjects }: SignedInLandingProps) {
  const navigate = Route.useNavigate();
  const { organization } = useSession();
  // A project needs an owner: no organization, no new project.
  const needsOrganization = organization === null;
  const [projects, setProjects] = useState<ProjectSummary[]>(
    initialProjects ?? [],
  );
  const [isLoadingProjects, setIsLoadingProjects] = useState(
    initialProjects === null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (initialProjects !== null) {
      setProjects(initialProjects);
      setIsLoadingProjects(false);
      setLoadError(null);
      return;
    }

    let cancelled = false;
    setIsLoadingProjects(true);
    setLoadError(null);

    (async () => {
      try {
        const res = await fetch("/api/projects", { credentials: "include" });
        if (!res.ok) {
          throw new Error(`Failed to fetch projects (${res.status})`);
        }
        const data = (await res.json()) as { projects: ProjectSummary[] };
        if (cancelled) {
          return;
        }
        setProjects(data.projects);
      } catch (error) {
        if (cancelled) {
          return;
        }
        setLoadError("We couldn't load your projects. Please try again.");
      } finally {
        if (!cancelled) {
          setIsLoadingProjects(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [initialProjects]);

  const all = groupProjects(projects, organization?.id ?? null);
  const isEmpty = all.own.length === 0 && all.shared.length === 0;
  const found = (project: ProjectSummary) => matches(project, search);

  const handleCreateProject = async () => {
    invariant(organization, "New project is disabled without an organization");
    setCreateError(null);
    setIsCreatingProject(true);
    try {
      const res = await fetch("/api/projects", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ organizationId: organization.id }),
      });
      if (!res.ok) {
        throw new Error(`Failed to create project (${res.status})`);
      }
      const data = (await res.json()) as { id: string };
      void navigate({
        to: "/p/$projectId/workshop",
        params: { projectId: data.id },
        search: {},
      });
    } catch (error) {
      setCreateError("We couldn't create a new project. Please try again.");
    } finally {
      setIsCreatingProject(false);
    }
  };

  const newProjectButton = (
    <Button
      onClick={handleCreateProject}
      disabled={isCreatingProject || needsOrganization}
    >
      {isCreatingProject ? <Loader2 className="animate-spin" /> : <Plus />}
      {isCreatingProject ? "Creating project…" : "New project"}
    </Button>
  );

  return (
    <div className="@container min-h-screen bg-muted/30">
      <header className="sticky top-0 z-10 flex h-14 items-center gap-3 border-b bg-background/85 px-4 backdrop-blur @lg:px-6">
        <BrandMark />
        <span className="h-5 w-px bg-border" />
        <OrganizationSwitcher />
        <div className="ml-auto">
          <UserMenu />
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl space-y-10 px-4 py-10 @lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
          <div className="flex w-full items-center gap-2 @2xl:w-auto">
            <InputGroup className="flex-1 bg-background @2xl:w-64">
              <InputGroupAddon>
                <Search />
              </InputGroupAddon>
              <InputGroupInput
                aria-label="Search projects"
                placeholder="Search projects"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </InputGroup>
            {needsOrganization ? null : newProjectButton}
          </div>
        </div>
        {loadError ? <ErrorNote>{loadError}</ErrorNote> : null}
        {createError ? <ErrorNote>{createError}</ErrorNote> : null}
        {needsOrganization ? <JoinOrganization /> : null}
        {isLoadingProjects ? (
          <ProjectGrid>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="aspect-[4/3] rounded-xl" />
            ))}
          </ProjectGrid>
        ) : (
          <>
            {organization && isEmpty ? (
              <Empty className="border border-dashed bg-background">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Building2 />
                  </EmptyMedia>
                  <EmptyTitle>Start your first project</EmptyTitle>
                  <EmptyDescription>
                    A project is where your team and Bear work together. Bring
                    in your drawings, specs and tools, and Bear takes on
                    coordination for the life of the job.
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>{newProjectButton}</EmptyContent>
              </Empty>
            ) : null}
            {organization && !isEmpty ? (
              <ProjectSection
                title={`In ${organization.name}`}
                projects={all.own.filter(found)}
                search={search}
              />
            ) : null}
            {all.shared.length > 0 ? (
              <ProjectSection
                title="Shared with you"
                icon={<Users className="size-3.5" />}
                projects={all.shared.filter(found)}
                detail={sharedLabel}
                search={search}
              />
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}

/** Creating a project needs an organization; opening a shared one doesn't. */
function JoinOrganization() {
  return (
    <Empty className="border bg-background">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Building2 />
        </EmptyMedia>
        <EmptyTitle>Join or create an organization</EmptyTitle>
        <EmptyDescription>
          An organization owns projects, and everyone in it can open them.
          Create one from the organization menu at the top, or accept an
          invitation.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <PendingInvitations />
      </EmptyContent>
    </Empty>
  );
}

function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
    >
      {children}
    </p>
  );
}

function ProjectGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid gap-4 @xl:grid-cols-2 @4xl:grid-cols-3 @6xl:grid-cols-4">
      {children}
    </div>
  );
}

interface ProjectSectionProps {
  title: string;
  icon?: ReactNode;
  projects: ProjectSummary[];
  detail?: (project: ProjectSummary) => string;
  search: string;
}

function ProjectSection({
  title,
  icon,
  projects,
  detail,
  search,
}: ProjectSectionProps) {
  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
        {icon}
        {title}
        <span className="text-xs tabular-nums opacity-70">
          {projects.length}
        </span>
      </h2>
      {projects.length === 0 ? (
        <p className="rounded-xl border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
          {search
            ? `No projects match “${search.trim()}”.`
            : "No projects here yet."}
        </p>
      ) : (
        <ProjectGrid>
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              detail={detail?.(project)}
            />
          ))}
        </ProjectGrid>
      )}
    </section>
  );
}

function ProjectCard({
  project,
  detail,
}: {
  project: ProjectSummary;
  detail?: string;
}) {
  return (
    <Link
      to="/p/$projectId/workshop"
      params={{ projectId: project.id }}
      search={{}}
      className="group overflow-hidden rounded-xl border bg-card shadow-xs transition hover:border-brand/50 hover:shadow-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
    >
      <div className="bg-blueprint relative flex aspect-[16/9] items-center justify-center border-b bg-muted/40">
        <span className="flex size-12 items-center justify-center rounded-xl border bg-background text-lg font-semibold text-brand shadow-xs transition group-hover:scale-105">
          {project.name.trim().slice(0, 1).toUpperCase()}
        </span>
      </div>
      <div className="space-y-0.5 px-4 py-3">
        <p className="truncate text-sm font-medium">{project.name}</p>
        {detail ? (
          <p className="truncate text-xs text-muted-foreground">{detail}</p>
        ) : null}
      </div>
    </Link>
  );
}

function UserMenu() {
  const user = useSignedInUser();
  const { signOut } = useSession();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full"
          aria-label="Account"
        >
          <Avatar className="size-8">
            <AvatarImage src={user.image ?? undefined} alt={user.name} />
            <AvatarFallback>
              {user.name.slice(0, 1).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="font-normal">
          <p className="truncate text-sm font-medium">{user.name}</p>
          <p className="truncate text-xs text-muted-foreground">{user.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/account" preload="intent">
            <UserRound className="mr-2 size-4" />
            Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void signOut()}>
          <LogOut className="mr-2 size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SignedOutLanding() {
  const { signIn } = useSession();
  return (
    <AuthShell>
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            Welcome to Bear
          </h1>
          <p className="text-sm text-muted-foreground">
            Sign in to pick up where you left off, or create an account to get
            started.
          </p>
        </div>
        <Button size="lg" className="w-full" onClick={signIn}>
          Sign in
        </Button>
      </div>
    </AuthShell>
  );
}
